const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DATE_TIME_WITHOUT_ZONE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?$/;

function isValidDate(date) {
    return date instanceof Date && !Number.isNaN(date.getTime());
}

function parseSchedulingDate(date) {
    if (date instanceof Date) {
        return new Date(date);
    }

    if (typeof date === "string" && DATE_TIME_WITHOUT_ZONE_PATTERN.test(date)) {
        return new Date(`${date}Z`);
    }

    return new Date(date);
}

function resolveOffsetMinutes(date, options = {}) {
    if (typeof options.timeZoneOffsetMinutes === "number") {
        return options.timeZoneOffsetMinutes;
    }

    return -date.getTimezoneOffset();
}

function deriveNorthAmericanOffsetFromScheduleDate(scheduleDate) {
    const actualDate = parseSchedulingDate(scheduleDate);
    if (!isValidDate(actualDate)) return null;

    const totalMinutes = actualDate.getUTCHours() * 60 + actualDate.getUTCMinutes();
    if (totalMinutes < 0 || totalMinutes > 14 * 60) return null;

    return -totalMinutes;
}

export function getScheduleDateOptions(scheduleDate, options = {}) {
    if (typeof options.timeZoneOffsetMinutes === "number") {
        return options;
    }

    const derivedOffset = deriveNorthAmericanOffsetFromScheduleDate(scheduleDate);
    return typeof derivedOffset === "number"
        ? { ...options, timeZoneOffsetMinutes: derivedOffset }
        : options;
}

function getOffsetDateParts(date, offsetMinutes) {
    const offsetDate = new Date(date.getTime() + offsetMinutes * 60 * 1000);

    return {
        year: offsetDate.getUTCFullYear(),
        month: offsetDate.getUTCMonth(),
        date: offsetDate.getUTCDate(),
        hours: offsetDate.getUTCHours(),
        minutes: offsetDate.getUTCMinutes(),
        seconds: offsetDate.getUTCSeconds(),
        milliseconds: offsetDate.getUTCMilliseconds(),
    };
}

function createDateFromOffsetParts(parts, offsetMinutes) {
    return new Date(Date.UTC(
        parts.year,
        parts.month,
        parts.date,
        parts.hours || 0,
        parts.minutes || 0,
        parts.seconds || 0,
        parts.milliseconds || 0
    ) - offsetMinutes * 60 * 1000);
}

export function getDateKey(date, options = {}) {
    if (typeof date === "string" && DATE_ONLY_PATTERN.test(date)) {
        return date;
    }

    const actualDate = parseSchedulingDate(date);
    if (!isValidDate(actualDate)) return null;

    const parts = getOffsetDateParts(actualDate, resolveOffsetMinutes(actualDate, options));
    const month = String(parts.month + 1).padStart(2, "0");
    const day = String(parts.date).padStart(2, "0");

    return `${parts.year}-${month}-${day}`;
}

function startOfDay(date, options = {}) {
    const actualDate = parseSchedulingDate(date);
    const offsetMinutes = resolveOffsetMinutes(actualDate, options);
    const parts = getOffsetDateParts(actualDate, offsetMinutes);

    return createDateFromOffsetParts({
        year: parts.year,
        month: parts.month,
        date: parts.date,
    }, offsetMinutes);
}

function addDays(date, days) {
    const copy = new Date(date);
    copy.setUTCDate(copy.getUTCDate() + days);
    return copy;
}

export function getScheduleServiceCategoryAvailabilityWindow(schedule, serviceCategory, options = {}) {
    const planningStart = parseSchedulingDate(schedule?.planningHorizon?.start);
    const planningEnd = parseSchedulingDate(schedule?.planningHorizon?.end);
    const serviceCategoryDate = parseSchedulingDate(serviceCategory?.date);

    if (!isValidDate(planningStart) || !isValidDate(planningEnd) || !isValidDate(serviceCategoryDate)) {
        return null;
    }

    const offsetMinutes = resolveOffsetMinutes(
        serviceCategoryDate,
        getScheduleDateOptions(serviceCategory?.date, options)
    );
    const serviceDateParts = getOffsetDateParts(serviceCategoryDate, offsetMinutes);
    const startTimeParts = getOffsetDateParts(planningStart, offsetMinutes);
    const endTimeParts = getOffsetDateParts(planningEnd, offsetMinutes);

    let dayStart = createDateFromOffsetParts({
        ...serviceDateParts,
        hours: startTimeParts.hours,
        minutes: startTimeParts.minutes,
        seconds: startTimeParts.seconds,
        milliseconds: startTimeParts.milliseconds,
    }, offsetMinutes);
    let dayEnd = createDateFromOffsetParts({
        ...serviceDateParts,
        hours: endTimeParts.hours,
        minutes: endTimeParts.minutes,
        seconds: endTimeParts.seconds,
        milliseconds: endTimeParts.milliseconds,
    }, offsetMinutes);

    if (dayEnd <= dayStart) {
        dayEnd = addDays(dayEnd, 1);
    }

    if (dayStart < planningStart) {
        dayStart = new Date(planningStart);
    }

    if (dayEnd > planningEnd) {
        dayEnd = new Date(planningEnd);
    }

    if (dayStart >= dayEnd) {
        return null;
    }

    return { start: dayStart, end: dayEnd, timeZoneOffsetMinutes: offsetMinutes };
}

export function getAvailableDayKeysForCategory({
    serviceCategories = [],
    selectedServiceCategoryId,
    availabilityByCategory = {},
    now = new Date(),
    options = {},
}) {
    const category = serviceCategories.find(
        (item) => (item.id || item.value) === selectedServiceCategoryId
    );

    if (!category?.timeSlot || !selectedServiceCategoryId) {
        return [];
    }

    const timeSlot = parseInt(category.timeSlot.value, 10);
    if (!Number.isFinite(timeSlot)) {
        return [];
    }

    const nowTime = new Date(now).getTime();
    const dayKeys = new Set();

    (availabilityByCategory[selectedServiceCategoryId] || []).forEach((period) => {
        const periodOptions = typeof period.timeZoneOffsetMinutes === "number"
            ? { ...options, timeZoneOffsetMinutes: period.timeZoneOffsetMinutes }
            : options;
        const todayStart = startOfDay(now, periodOptions);
        let periodStart = parseSchedulingDate(period.start);
        const periodEnd = parseSchedulingDate(period.end);

        if (!isValidDate(periodStart) || !isValidDate(periodEnd)) return;
        if (periodEnd <= todayStart) return;

        if (getDateKey(periodStart, periodOptions) === getDateKey(now, periodOptions) && periodStart.getTime() < nowTime) {
            periodStart = parseSchedulingDate(now);
        }

        const availableMinutes = (periodEnd.getTime() - periodStart.getTime()) / 60000;
        if (availableMinutes >= timeSlot) {
            const key = getDateKey(periodStart, periodOptions);
            if (key) dayKeys.add(key);
        }
    });

    return [...dayKeys];
}

export function filterSlotsForSelectedDay(slots = [], selectedDate, { now = new Date(), options = {} } = {}) {
    const selectedDayKey = getDateKey(selectedDate, options);
    if (!selectedDayKey || !Array.isArray(slots)) {
        return [];
    }

    const selectedIsToday = selectedDayKey === getDateKey(now, options);
    const nowTime = parseSchedulingDate(now).getTime();

    return slots.filter((slot) => {
        const start = parseSchedulingDate(slot?.start);
        const end = parseSchedulingDate(slot?.end);

        if (!isValidDate(start) || !isValidDate(end)) {
            return false;
        }

        if (getDateKey(start, options) !== selectedDayKey) {
            return false;
        }

        if (selectedIsToday && start.getTime() <= nowTime) {
            return false;
        }

        return true;
    });
}

export function filterSlotsFromDayForward(slots = [], selectedDate, { now = new Date(), options = {} } = {}) {
    const selectedDayKey = getDateKey(selectedDate, options);
    if (!selectedDayKey || !Array.isArray(slots)) {
        return [];
    }

    const nowKey = getDateKey(now, options);
    const nowTime = parseSchedulingDate(now).getTime();

    return slots.filter((slot) => {
        const start = parseSchedulingDate(slot?.start);
        const end = parseSchedulingDate(slot?.end);

        if (!isValidDate(start) || !isValidDate(end)) {
            return false;
        }

        const slotDayKey = getDateKey(start, options);
        if (!slotDayKey || slotDayKey < selectedDayKey) {
            return false;
        }

        if (slotDayKey === nowKey && start.getTime() <= nowTime) {
            return false;
        }

        return true;
    });
}

export const SCHEDULE_BLOCKING_SLOT_STATUSES = [
    "busy-meeting",
    "busy-unavailable",
    "busy-training",
    "busy-documentation",
    "busy-meal",
    "busy-administration",
    "busy-unscheduled",
    "busy-break",
    "busy-other",
];

function subtractTimePeriod(availabilityPeriods, slotStart, slotEnd) {
    const updated = [];

    for (const period of availabilityPeriods) {
        if (slotEnd <= period.start || slotStart >= period.end) {
            updated.push(period);
        } else {
            if (slotStart > period.start) {
                updated.push({ start: period.start, end: slotStart });
            }
            if (slotEnd < period.end) {
                updated.push({ start: slotEnd, end: period.end });
            }
        }
    }

    return updated;
}

function mergeTimePeriods(periods) {
    if (!periods.length) return [];

    const sortedPeriods = [...periods].sort((a, b) => a.start - b.start);
    const merged = [sortedPeriods[0]];

    for (let i = 1; i < sortedPeriods.length; i++) {
        const last = merged[merged.length - 1];
        const current = sortedPeriods[i];

        if (current.start <= last.end) {
            last.end = new Date(Math.max(last.end, current.end));
        } else {
            merged.push(current);
        }
    }

    return merged;
}

function getSlotServiceCategoryTypes(slot) {
    return (slot?.serviceCategory || [])
        .map((item) => item?.id
            ? { id: item.id, name: item.name }
            : { id: item?.coding?.[0]?.code, name: item?.coding?.[0]?.display })
        .filter((item) => item.id);
}

function getSlotStartTime(slot) {
    const start = parseSchedulingDate(slot?.start);
    return isValidDate(start) ? start.getTime() : Number.POSITIVE_INFINITY;
}

export function sortSlotsByStart(slots = []) {
    return [...(slots || [])].sort((a, b) => getSlotStartTime(a) - getSlotStartTime(b));
}

function slotsRepresentSameTime(left, right) {
    const leftStart = parseSchedulingDate(left?.start);
    const rightStart = parseSchedulingDate(right?.start);
    const leftEnd = parseSchedulingDate(left?.end);
    const rightEnd = parseSchedulingDate(right?.end);

    if (!isValidDate(leftStart) || !isValidDate(rightStart)) {
        return false;
    }

    return leftStart.getTime() === rightStart.getTime()
        && (!isValidDate(leftEnd) || !isValidDate(rightEnd) || leftEnd.getTime() === rightEnd.getTime());
}

export function mergeSlotsByStart(availableSlots = [], reservedSlots = []) {
    const merged = [...(availableSlots || [])];

    (reservedSlots || []).forEach((reservedSlot) => {
        if (!merged.some((slot) => slotsRepresentSameTime(slot, reservedSlot))) {
            merged.push(reservedSlot);
        }
    });

    return sortSlotsByStart(merged);
}

export function mergeCategoryIdsByDay(base = {}, incoming = {}) {
    const merged = { ...(base || {}) };

    Object.keys(incoming || {}).forEach((dayKey) => {
        const existing = Array.isArray(merged[dayKey]) ? [...merged[dayKey]] : [];
        const next = Array.isArray(incoming[dayKey]) ? incoming[dayKey] : [];

        next.forEach((categoryId) => {
            if (!existing.includes(categoryId)) {
                existing.push(categoryId);
            }
        });

        merged[dayKey] = existing;
    });

    return merged;
}

function orgIdsMatch(scheduleOrgId, selectedOrgId) {
    if (!selectedOrgId) return true;
    if (!scheduleOrgId) return false;

    const scheduleOrg = String(scheduleOrgId);
    const selectedOrg = String(selectedOrgId);

    return scheduleOrg === selectedOrg
        || scheduleOrg.startsWith(`${selectedOrg}.`)
        || selectedOrg.startsWith(`${scheduleOrg}.`);
}

export function scheduleMatchesSelectedOrg(schedule, orgId) {
    if (!orgId) return true;
    if (orgIdsMatch(schedule?.orgId, orgId)) return true;

    return (schedule?.serviceCategory || []).some((category) => orgIdsMatch(category?.orgId, orgId));
}

export function computeServiceCategoryAvailabilities(schedules = [], options = {}) {
    const serviceCategoryAvailabilities = {};

    for (const schedule of schedules) {
        const scheduleAvailabilities = {};

        for (const serviceCategory of schedule?.serviceCategory || []) {
            if (!serviceCategory?.id) continue;

            const availabilityWindow = getScheduleServiceCategoryAvailabilityWindow(
                schedule,
                serviceCategory,
                options
            );

            if (!availabilityWindow) continue;

            if (!scheduleAvailabilities[serviceCategory.id]) {
                scheduleAvailabilities[serviceCategory.id] = [];
            }

            scheduleAvailabilities[serviceCategory.id].push(availabilityWindow);
        }

        for (const slot of schedule?.slots || []) {
            const slotStart = parseSchedulingDate(slot.start);
            const slotEnd = parseSchedulingDate(slot.end);

            if (!isValidDate(slotStart) || !isValidDate(slotEnd)) continue;

            if (slot.status === "busy-reserved") {
                const reservedTypes = getSlotServiceCategoryTypes(slot);

                for (const categoryId in scheduleAvailabilities) {
                    if (!reservedTypes.some((category) => category.id === categoryId)) {
                        scheduleAvailabilities[categoryId] = subtractTimePeriod(
                            scheduleAvailabilities[categoryId],
                            slotStart,
                            slotEnd
                        );
                    }
                }

                for (const category of reservedTypes) {
                    scheduleAvailabilities[category.id] = [
                        ...(scheduleAvailabilities[category.id] || []),
                        { start: slotStart, end: slotEnd },
                    ];
                }
            } else if (SCHEDULE_BLOCKING_SLOT_STATUSES.includes(slot.status)) {
                for (const categoryId in scheduleAvailabilities) {
                    scheduleAvailabilities[categoryId] = subtractTimePeriod(
                        scheduleAvailabilities[categoryId],
                        slotStart,
                        slotEnd
                    );
                }
            }
        }

        for (const categoryId in scheduleAvailabilities) {
            serviceCategoryAvailabilities[categoryId] = [
                ...(serviceCategoryAvailabilities[categoryId] || []),
                ...scheduleAvailabilities[categoryId],
            ];
        }
    }

    for (const categoryId in serviceCategoryAvailabilities) {
        serviceCategoryAvailabilities[categoryId] = mergeTimePeriods(
            serviceCategoryAvailabilities[categoryId]
        );
    }

    return serviceCategoryAvailabilities;
}
